import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { StatsApi } from '@/services/stats/statsApi'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import type { StatsRow } from '../components/statsColumns'

/** One line item's metrics, keyed by metric name as the API reports them. */
export type LineItemStatsRow = StatsRow

interface LineItemStatsChunk {
  lineItem?: { id?: string }
  [key: string]: unknown
}

/** The whole of the ad server's history, for the "all time" choice. */
const ALL_TIME_START = '2010-01-01T00:00:00Z'

/**
 * Totals for every line item on an order, keyed by line item id. The list only
 * shows one number per metric, so the query is not chunked over time.
 *
 * Passing `null` as the range totals the order's whole history instead of a
 * period.
 */
export function useLineItemStats(orderId: string, range: DateRange | null, enabled = true) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const currency = currentNetwork?.defaultCurrency ?? 'NOK'
  const hasToken = AuthService.isAuthenticated()

  const { startDate, endDate } = useMemo(() => {
    if (range) return getDateRange(range, timeZone)
    return { startDate: ALL_TIME_START, endDate: new Date().toISOString() }
  }, [range, timeZone])

  const query = useQuery({
    queryKey: ['lineitem-stats', orderId, range ?? 'all', startDate, endDate, currency],
    queryFn: () =>
      StatsApi.get({
        startDate,
        endDate,
        currency,
        groupBy: 'LINE_ITEM',
        orderId,
        timeout: 180000,
      }),
    enabled: enabled && hasToken && !!currentNetwork && !!orderId,
    staleTime: 60 * 1000,
  })

  const statsByLineItem = useMemo(() => {
    const chunks = (query.data?.chunks ?? []) as LineItemStatsChunk[]
    const byId: Record<string, LineItemStatsRow> = {}
    for (const chunk of chunks) {
      const id = chunk.lineItem?.id
      if (id) byId[id] = chunk as LineItemStatsRow
    }
    return byId
  }, [query.data])

  return {
    statsByLineItem,
    /**
     * The order's figures for the period, as the ad server totals them. Taken
     * from the response rather than added up here: eCpm and viewability follow
     * the network's own eCpm and viewability methods, which only the server
     * knows.
     */
    totals: (query.data?.totals ?? undefined) as LineItemStatsRow | undefined,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  }
}
